import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY, setUnauthorizedHandler } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [isLoading, setIsLoading] = useState(true);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
    }, [setToken, setUser]);

    // Wire 401 handler so expired tokens clear state app-wide.
    useEffect(() => {
        setUnauthorizedHandler(() => logout());
    }, [logout]);

    // Hydrate user from /api/auth/me on boot if a token exists.
    // Intentionally runs once on mount — closes over stable module-level
    // imports (api, TOKEN_KEY) and stable React state setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const t = localStorage.getItem(TOKEN_KEY);
        if (!t) {
            setIsLoading(false);
            return;
        }
        let cancelled = false;
        api.get("/auth/me")
            .then((res) => {
                if (cancelled) return;
                setUser(res.data);
                setToken(t);
            })
            .catch(() => {
                if (cancelled) return;
                localStorage.removeItem(TOKEN_KEY);
                setToken(null);
                setUser(null);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const login = useCallback(async (email, password) => {
        const res = await api.post("/auth/login", { email, password });
        localStorage.setItem(TOKEN_KEY, res.data.token);
        setToken(res.data.token);
        setUser(res.data.user);
        return res.data.user;
    }, [setToken, setUser]);

    const register = useCallback(async (email, password, fullName) => {
        const res = await api.post("/auth/register", {
            email,
            password,
            full_name: fullName,
        });
        localStorage.setItem(TOKEN_KEY, res.data.token);
        setToken(res.data.token);
        setUser(res.data.user);
        return res.data.user;
    }, [setToken, setUser]);

    const changePassword = useCallback(async (oldPassword, newPassword) => {
        await api.post("/auth/change-password", {
            old_password: oldPassword,
            new_password: newPassword,
        });
    }, []);

    const updateProfile = useCallback(async (fullName) => {
        const res = await api.patch("/auth/me", { full_name: fullName });
        setUser(res.data);
        return res.data;
    }, [setUser]);

    // Phase 15 — refresh the cached user (post-payment, subscription state changes).
    const refreshUser = useCallback(async () => {
        const res = await api.get("/auth/me");
        setUser(res.data);
        return res.data;
    }, [setUser]);

    const isSubscribed = (() => {
        const sub = user?.subscription;
        if (!sub || sub.status !== "active") return false;
        if (!sub.expires_at) return false;
        return new Date(sub.expires_at) > new Date();
    })();

    const value = {
        user,
        token,
        isLoading,
        isAuthenticated: !!user,
        isSubscribed,
        isAdmin: user?.role === "admin",
        login,
        register,
        logout,
        changePassword,
        updateProfile,
        refreshUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return ctx;
};
